import type {
  Edge as ReactFlowEdge,
  Node as ReactFlowNode,
} from "@xyflow/react";
import type { ParsedFlowchart } from "./parse-mermaid-flowchart";
import { resolveFlowchart } from "./resolve-flowchart";
import type { FlowchartEdgeData, FlowchartNodeData } from "./flowchart-types";
import { buildFlowchartContentKey } from "./flowchart-content-key";
import {
  buildBoxAndPresentationMaps,
  createLaidOutDagreGraph,
  getGraphSize,
  getOrderedSubgraphs,
} from "./flowchart-layout-dagre";
import {
  buildChildNodes,
  buildDegreeMap,
  buildEdges,
  buildGroupNodes,
  getParallelGroups,
  getSourcePosition,
  getStartNodeId,
  getTargetPosition,
} from "./flowchart-layout-elements";

export { FLOWCHART_DAGRE_GRAPH_OPTIONS } from "./flowchart-layout-dagre";

export interface LayoutResult {
  nodes: ReactFlowNode<FlowchartNodeData>[];
  edges: ReactFlowEdge<FlowchartEdgeData>[];
  graphWidth: number;
  graphHeight: number;
  startNodeId: string | null;
  contentKey: string;
}

export function buildReactFlowElements(parsed: ParsedFlowchart): LayoutResult {
  const resolved = resolveFlowchart(parsed);
  const dagreLayout = createLaidOutDagreGraph({ resolved });
  const subgraphById = new Map(
    resolved.subgraphs.map((subgraph) => [subgraph.id, subgraph]),
  );
  const orderedSubgraphs = getOrderedSubgraphs({ resolved, subgraphById });
  const sourcePosition = getSourcePosition(resolved.direction);
  const targetPosition = getTargetPosition(resolved.direction);
  const degreeMap = buildDegreeMap({
    nodes: resolved.nodes,
    edges: resolved.edges,
  });
  const parallelGroups = getParallelGroups(resolved.edges);
  const { nodeBoxesById, presentationById } = buildBoxAndPresentationMaps({
    resolved,
    dagreGraph: dagreLayout.dagreGraph,
    orderedSubgraphs,
    subgraphSizeById: dagreLayout.subgraphSizeById,
    nodeSizeById: dagreLayout.nodeSizeById,
  });

  const groupNodes = buildGroupNodes({
    orderedSubgraphs,
    subgraphById,
    nodeBoxesById,
    presentationById,
  });
  const childNodes = buildChildNodes({
    nodes: resolved.nodes,
    nodeBoxesById,
    presentationById,
    degreeMap,
    sourcePosition,
    targetPosition,
  });
  const edges = buildEdges({ resolved, presentationById, parallelGroups });
  const graphSize = getGraphSize({
    dagreGraph: dagreLayout.dagreGraph,
    boxes: Array.from(nodeBoxesById.values()),
  });

  return {
    nodes: [...groupNodes, ...childNodes],
    edges,
    graphWidth: graphSize.width,
    graphHeight: graphSize.height,
    startNodeId: getStartNodeId(childNodes),
    contentKey: buildFlowchartContentKey(parsed),
  };
}
